import { selectDesignProperties } from '@/shared/utils/common/selectDesignProperties';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import PropTypes from 'prop-types';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
} from 'react-hook-form';
import { Tooltip } from 'antd';
import { TooltipPlacement } from 'antd/es/tooltip';

type Props<T extends FieldValues> = {
  name: Path<T>;
  label?: string;
  className?: string;
  id?: string;
  register?: object;
  options?: optionsType[];
  error?: boolean;
  errorMessage?: string;
  required: boolean;
  disabled?: boolean;
  style?: React.CSSProperties | null;
  multiple?: boolean;
  control: Control<T>;
  tooltipProps?: TooltipProps;
};
type TooltipProps = {
  title: string;
  color?: string;
  trigger?: ActionType | ActionType[];
  bodyClassName?: string;
  placement?: TooltipPlacement;
};
type optionsType = {
  value: number | string;
  label: string;
};

type ActionType = 'click' | 'hover' | 'focus' | 'contextMenu';
export const CustomSelect = <T extends FieldValues>({
  name,
  label,
  className,
  id,
  register,
  options,
  error,
  errorMessage,
  required,
  disabled,
  style,
  multiple = false,
  control,
  tooltipProps,
}: Props<T>) => {
  const getSingleSelectedValue = (value: PathValue<T, Path<T>>) => {
    return value === '' ? '' : value;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl variant="filled">
          <InputLabel
            id={`${id}-label`}
            required={required}
            style={{ color: '#3f3f3f', fontSize: '16px', fontWeight: 'bolder' }}
            htmlFor={id}
          >
            {label}
          </InputLabel>
          <Tooltip
            title={!error ? tooltipProps?.title : null}
            color={tooltipProps?.color}
            trigger={tooltipProps?.trigger}
            classNames={{
              body: tooltipProps?.bodyClassName,
            }}
            placement={tooltipProps?.placement}
          >
            <Select
              labelId={`${id}-label`}
              id={id}
              className={className}
              {...register}
              style={{
                ...style,
                backgroundColor: style?.backgroundColor ?? 'white',
                border: `1px solid ${error || errorMessage ? 'red' : '#c4c4c4'}`,
              }}
              error={error}
              label={label}
              name={field.name}
              value={
                multiple
                  ? field.value?.length > 0
                    ? field.value
                    : []
                  : getSingleSelectedValue(field.value)
              }
              defaultValue={''}
              multiple={multiple}
              disabled={disabled}
              sx={{ ...selectDesignProperties }}
              aria-label={label}
              data-testid="add-to-tenants"
            >
              {!multiple && <MenuItem value="">Select {label}</MenuItem>}

              {options?.map(option => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  data-testid={`tenant-${option.value}`}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Tooltip>

          {errorMessage && (
            <FormHelperText
              style={{ color: 'red', margin: '0', maxWidth: '260px' }}
            >
              {' '}
              {errorMessage}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

CustomSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  register: PropTypes.object,
  options: PropTypes.array,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  required: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  style: PropTypes.object,
  multiple: PropTypes.bool,
  control: PropTypes.object.isRequired,
  tooltipProps: PropTypes.object,
};
